import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Link,
  IconButton,
  InputAdornment,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Person,
  Lock,
  CalendarToday,
  Code,
  Google,
  ArrowBack,
  PhotoCamera,
  Delete
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContextNew';
import { authAPI } from '../../services/api';
import CountryCodeSelector from '../../components/CountryCodeSelector';
import { getCountryCode, getCountryFields } from '../../utils/countryUtils';
import { getTimezoneForCountry } from '../../utils/timezoneUtils';
import './AuthPages.css';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    referralCode: '',
    country: getCountryCode() // Auto-detect country
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const dateInputRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  
  const { register, updateAvatar, login, setAuthState } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Extract referral code from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const referralCode = urlParams.get('ref');
    
    if (referralCode) {
      setFormData(prevFormData => ({
        ...prevFormData,
        referralCode: referralCode
      }));
      console.log('✅ Referral code extracted from URL:', referralCode);
    }
  }, [location.search]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }

      setProfilePhoto(file);
      setProfilePhotoPreview(URL.createObjectURL(file));
      setError(''); // Clear any previous errors
    }
  };

  const handleRemovePhoto = () => {
    setProfilePhoto(null);
    setProfilePhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCalendarIconClick = () => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim() || 
        !formData.email.trim() || 
        !formData.password || !formData.confirmPassword) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Phone validation
    if (!phoneNumber || phoneNumber.length < 8) {
      setError('Please enter a valid phone number');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Get country-specific currency and timezone
      const countryFields = getCountryFields(formData.country);
      const timezone = getTimezoneForCountry(formData.country);
      
      // Prepare data for backend
      const signupData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: phoneNumber,
        password: formData.password,
        dateOfBirth: formData.dateOfBirth || null,
        referralCode: formData.referralCode.trim() || null,
        location: {
          country: formData.country
        },
        preferences: {
          currency: countryFields.currency,
          timezone: timezone
        }
      };

      // Use register from AuthContext for signup without photo (handles token and auto-login)
      // For signup with photo, use API directly then manually handle auth state
      let response;
      let signupSuccess = false;
      
      if (profilePhoto) {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('profilePhoto', profilePhoto);
        
        // Add other form fields
        Object.keys(signupData).forEach(key => {
          formData.append(key, signupData[key]);
        });
        
        response = await authAPI.customerSignupWithPhoto(formData);
        
        if (response.success && response.data?.token) {
          // Extract token and user data from response
          const { token, ...userData } = response.data;
          
          // Transform userData to match AuthContext expectations
          // Backend returns customerId, but AuthContext might expect id or _id
          const transformedUserData = {
            ...userData,
            id: userData.customerId || userData.id || userData._id,
            _id: userData.customerId || userData.id || userData._id
          };
          
          // Update AuthContext state directly to ensure immediate authentication
          // This mimics what AuthContext.register does
          setAuthState(token, transformedUserData);
          
          // Update avatar if profile photo was uploaded
          if (response.data.profilePhotoUrl || response.data.avatar) {
            try {
              await updateAvatar(response.data.profilePhotoUrl || response.data.avatar);
            } catch (avatarError) {
              console.warn('Avatar update failed:', avatarError);
              // Don't fail signup if avatar update fails
            }
          }
          
          signupSuccess = true;
        }
      } else {
        // Use register from AuthContext which handles token extraction and auto-login
        const registerResult = await register(signupData);
        signupSuccess = registerResult.success;
        if (!signupSuccess) {
          response = { success: false, error: registerResult.error };
        } else {
          response = { success: true };
        }
      }
      
      if (signupSuccess) {
        if (formData.country) {
          localStorage.setItem('countryCode', String(formData.country).toUpperCase());
        }
        // Profile photo is already handled in the signup request if provided
        if (profilePhoto) {
          setSuccess('Account created and profile photo uploaded successfully! Redirecting...');
        } else {
          setSuccess('Account created successfully! Redirecting...');
        }
        
        // Trigger cart sync immediately after signup
        // Dispatch custom event to notify CartContext that user just signed up
        window.dispatchEvent(new CustomEvent('userAuthenticated', { 
          detail: { source: 'signup' } 
        }));
        
        // Small delay to ensure auth state is updated, then redirect
        setTimeout(() => {
          // Check if there's a return URL to redirect to
          const returnUrl = location.state?.returnUrl;
          
          // Navigate to returnUrl or home
          // AuthContext state is already updated via updateUser, so we can just navigate
          if (returnUrl && returnUrl !== '/login' && returnUrl !== '/signup') {
            navigate(returnUrl);
          } else {
            navigate('/');
          }
        }, 1500);
      } else {
        setError(response.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.error || error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0A2A66 0%, #1a3a76 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: 20,
          zIndex: 10,
          width: 120,
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <img
          src="/logo3.png"
          alt="Logo"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain'
          }}
        />
      </Box>

      {/* Background Shape */}
      <Box
        sx={{
          position: 'absolute',
          top: 40,
          left: 16,
          width: 500,
          height: '60vw',
          maxHeight: 400,
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
          transform: 'rotate(-5deg)',
          zIndex: 1
        }}
      />

      <Container
        maxWidth="sm"
        sx={{
          position: 'relative',
          zIndex: 2,
          pt: { xs: 8, md: 12 },
          pb: 4
        }}
      >
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                color: '#00040B',
                mb: 1,
                fontSize: { xs: '1.75rem', md: '2rem' }
              }}
            >
              Create Your Account
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                fontSize: { xs: '0.9rem', md: '1rem' }
              }}
            >
              Join our community and start exploring amazing services
            </Typography>
          </Box>

          {/* Signup Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
            {/* Profile Photo Field */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#00040B', fontWeight: 600 }}>
                Profile Photo
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                <Avatar
                  src={profilePhotoPreview}
                  sx={{
                    width: 80,
                    height: 80,
                    border: '3px solid #006AF9',
                    backgroundColor: profilePhotoPreview ? 'transparent' : '#f0f0f0'
                  }}
                >
                  {!profilePhotoPreview && <Person sx={{ fontSize: 40, color: '#666' }} />}
                </Avatar>
                <Stack spacing={1}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    style={{ display: 'none' }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCamera />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      borderColor: '#006AF9',
                      color: '#006AF9',
                      '&:hover': {
                        borderColor: '#0056CC',
                        backgroundColor: 'rgba(0, 106, 249, 0.04)',
                      },
                    }}
                  >
                    {profilePhotoPreview ? 'Change Photo' : 'Upload Photo'}
                  </Button>
                  {profilePhotoPreview && (
                    <Button
                      variant="outlined"
                      startIcon={<Delete />}
                      onClick={handleRemovePhoto}
                      sx={{
                        borderColor: '#dc3545',
                        color: '#dc3545',
                        '&:hover': {
                          borderColor: '#c82333',
                          backgroundColor: 'rgba(220, 53, 69, 0.04)',
                        },
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </Stack>
              </Stack>
              <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
                Upload a profile photo to personalize your account (max 5MB)
              </Typography>
            </Box>

            {/* Name Fields */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name *"
                  placeholder="Enter your first name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa',
                      '&:hover fieldset': {
                        borderColor: '#006AF9',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#006AF9',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: '#006AF9' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name *"
                  placeholder="Enter your last name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa',
                      '&:hover fieldset': {
                        borderColor: '#006AF9',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#006AF9',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: '#006AF9' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Email Field */}
            <TextField
              fullWidth
              label="Email Address *"
              placeholder="Enter your email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              variant="outlined"
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  '&:hover fieldset': {
                    borderColor: '#006AF9',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#006AF9',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#006AF9' }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Phone Field with Country Code */}
            <CountryCodeSelector
              value={phoneNumber}
              onChange={setPhoneNumber}
              label="Phone Number"
              placeholder="Enter your phone number"
              required
              helperText="Enter your mobile number with country code"
              defaultCountry={formData.country || getCountryCode()}
              sx={{ mb: 2 }}
            />

            {/* Date of Birth Field */}
            <TextField
              fullWidth
              label="Date of Birth"
              name="dateOfBirth"
              type="date"
              value={formData.dateOfBirth}
              onChange={handleChange}
              variant="outlined"
              inputRef={dateInputRef}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  '&:hover fieldset': {
                    borderColor: '#006AF9',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#006AF9',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      onClick={handleCalendarIconClick}
                      edge="start"
                      sx={{ 
                        color: '#006AF9',
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 106, 249, 0.08)',
                        }
                      }}
                    >
                      <CalendarToday />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              inputProps={{
                max: new Date().toISOString().split('T')[0], // Prevent future dates
              }}
            />

            {/* Country Selection Field */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Country *</InputLabel>
              <Select
                value={formData.country}
                onChange={(e) => handleChange({ target: { name: 'country', value: e.target.value } })}
                label="Country *"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                    '&:hover fieldset': {
                      borderColor: '#006AF9',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#006AF9',
                    },
                  },
                }}
              >
                <MenuItem value="AU">Australia</MenuItem>
                <MenuItem value="IN">India</MenuItem>
                <MenuItem value="AE">United Arab Emirates</MenuItem>
                <MenuItem value="US">United States</MenuItem>
                <MenuItem value="SG">Singapore</MenuItem>
                <MenuItem value="GB">United Kingdom</MenuItem>
                <MenuItem value="MY">Malaysia</MenuItem>
              </Select>
            </FormControl>

            {/* Referral Code Field */}
            <TextField
              fullWidth
              label="Referral Code (Optional)"
              placeholder="Enter referral code if you have one"
              name="referralCode"
              value={formData.referralCode}
              onChange={handleChange}
              variant="outlined"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  '&:hover fieldset': {
                    borderColor: '#006AF9',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#006AF9',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Code sx={{ color: '#006AF9' }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Field */}
            <TextField
              fullWidth
              label="Password *"
              placeholder="Enter your password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              variant="outlined"
              required
              helperText="Password must be at least 8 characters long"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  '&:hover fieldset': {
                    borderColor: '#006AF9',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#006AF9',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#666' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Confirm Password Field */}
            <TextField
              fullWidth
              label="Confirm Password *"
              placeholder="Confirm your password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              variant="outlined"
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  '&:hover fieldset': {
                    borderColor: '#006AF9',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#006AF9',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: '#666' }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Error Message */}
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* Success Message */}
            {success && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                {success}
              </Alert>
            )}

            {/* Signup Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                backgroundColor: '#006AF9',
                background: 'linear-gradient(135deg, #006AF9 0%, #0056CC 100%)',
                boxShadow: '0 4px 15px rgba(0, 106, 249, 0.3)',
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0056CC 0%, #004499 100%)',
                  boxShadow: '0 6px 20px rgba(0, 106, 249, 0.4)',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  boxShadow: 'none',
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Create Account'
              )}
            </Button>
          </Box>

          {/* Sign In Link */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#666', display: 'inline' }}>
              Already have an account?{' '}
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={handleBackToLogin}
              sx={{
                color: '#006AF9',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Sign In
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SignupPage; 